<template>
  <q-table
    :columns="computedCols"
    :rows="rows"
    class="external-dns-table"
    :class="{ empty: rows.length === 0 }"
    ref="itemsTable"
    row-key="id"
    flat
    v-model:pagination="pagination"
    :loading="loading"
    binary-state-sort
    :rows-per-page-options="rowsPerPageOptions"
    @request="load"
  >
    <template v-slot:no-data>
      <div class="flex full-width column items-center q-py-lg">
        <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
        <span class="block" style="font-size: 14px">No items were found.</span>
      </div>
    </template>
    <template v-slot:body-cell-status="props">
      <q-td style="width: 30px" :props="props">
        <StatusCell v-if="!props.row.deleted_at" :status="props.row.status" />
        <q-icon v-else name="close" color="grey" size="15px"></q-icon>
      </q-td>
    </template>
    <template v-slot:body-cell-source_apm_id="props">
      <q-td :props="props">
        <router-link :to="`/my-portfolio/${props.row.source_apm_id}`">{{ props.row.source_apm_id }}</router-link>
      </q-td>
    </template>
    <template v-slot:body-cell-target_apm_id="props">
      <q-td :props="props">
        <router-link :to="`/my-portfolio/${props.row.target_apm_id}`">{{ props.row.target_apm_id }}</router-link>
      </q-td>
    </template>
    <template v-slot:body-cell-source_ip="props">
      <q-td :props="props">
        <div class="long-desc cursor-pointer" @click="copyValue(props.row.source_ip)">
          {{ props.row.source_ip }}
          <q-tooltip
            ><span v-if="props.row.source_ip.length > 45">{{ props.row.source_ip }}<br /></span>Click to copy</q-tooltip
          >
        </div>
      </q-td>
    </template>
    <template v-slot:body-cell-target_ip="props">
      <q-td :props="props">
        <div class="long-desc cursor-pointer" @click="copyValue(props.row.target_ip)">
          {{ props.row.target_ip }}
          <q-tooltip
            ><span v-if="props.row.target_ip.length > 45">{{ props.row.target_ip }}<br /></span>Click to copy</q-tooltip
          >
        </div>
      </q-td>
    </template>
    <template v-slot:body-cell-comment="props">
      <q-td :props="props">
        <div class="long-desc short">
          {{ props.row.comment }}
          <q-tooltip v-if="props.row.comment?.length > 15">{{ props.row.comment }}</q-tooltip>
        </div>
      </q-td>
    </template>
    <template v-slot:body-cell-last_change_link="props">
      <q-td :props="props">
        <a v-if="props.row.last_change_link" target="_blank" :href="props.row.last_change_link">{{
          props.row.last_change_number || 'Change Request'
        }}</a>
        <span v-else>-</span>
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <q-btn
          v-if="mode === 'list'"
          flat
          round
          size="8px"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
          :ripple="false"
          icon="engp:engp-ico-registry_outline"
          @click.stop="goToRegistration($event, props.row)"
        >
          <q-tooltip>Go to registration</q-tooltip>
        </q-btn>
        <q-btn
          v-if="mode === 'edit' && props.row.source_ip && props.row.source_ip !== '-'"
          flat
          round
          size="10px"
          style="width: 30px; height: 40px"
          class="btn--no-hover"
          :ripple="false"
          icon="sym_o_difference"
          @click.stop="$emit('duplicate', props.row)"
        >
          <q-tooltip>Duplicate Firewall Rule (EAR) request</q-tooltip>
        </q-btn>
        <WorkflowsBtn
          @showWorkflow="showWorkflow($event, props.row)"
          :loading="loading"
          :relatedWorkflowStatuses="relatedWorkflowStatuses.find((i) => i.id === props.row.id)?.statuses || []"
        />
        <CopyBtn :copyData="props.row.id" notification="Firewall Rule (EAR) request UUID was copied" />
      </q-td>
    </template>
  </q-table>
</template>

<script>
import { copyToClipboard } from 'quasar';
import dayjs from 'dayjs';
import { supportCntrlClick } from '../helpers';
import { getFirewallRules } from '@/pages/allItems/api/api';
import { getRelatedWorkflowsStatuses } from '@/pages/registrations/api/api';
import StatusCell from '@/pages/registrations/components/registration-network/components/StatusCell.vue';
import CopyBtn from './CopyBtn.vue';
import WorkflowsBtn from './WorkflowsBtn.vue';

export default {
  name: 'FirewallRulesTable',
  components: {
    CopyBtn,
    StatusCell,
    WorkflowsBtn,
  },
  props: {
    rowsPerPageOptions: {
      type: Array,
      default: () => [5, 7, 10, 15, 20],
    },
    filters: {
      type: Object,
    },
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
    mode: {
      type: String,
    },
    showIps: {
      type: Boolean,
    },
  },
  data() {
    return {
      perPageOptions: this.rowsPerPageOptions,
      relatedWorkflowStatuses: [],
      rows: [],
      loading: false,
      pagination: {
        sortBy: 'created_at',
        descending: true,
        page: 1,
        rowsPerPage: this.mode === 'list' ? 20 : 5,
      },
      segmentCols: [
        {
          label: 'Source name',
          name: 'source_service_name',
          field: 'source_service_name',
          align: 'left',
        },
        {
          label: 'Target name',
          name: 'target_service_name',
          field: 'target_service_name',
          align: 'left',
        },
        {
          label: 'Source location',
          name: 'source_location',
          field: 'source_location',
          align: 'left',
        },
        {
          label: 'Target location',
          name: 'target_location',
          field: 'target_location',
          align: 'left',
        },
        {
          label: 'Source env',
          name: 'source_env',
          field: 'source_env',
          align: 'left',
        },
        {
          label: 'Target env',
          name: 'target_env',
          field: 'target_env',
          align: 'left',
        },
      ],
      ipCols: [
        {
          label: 'Source IP',
          name: 'source_ip',
          field: 'source_ip',
          align: 'left',
        },
        {
          label: 'Target IP',
          name: 'target_ip',
          field: 'target_ip',
          align: 'left',
        },
      ],
      baseCols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Source APMID',
          name: 'source_apm_id',
          field: 'source_apm_id',
          align: 'left',
        },
        {
          label: 'Target APMID',
          name: 'target_apm_id',
          field: 'target_apm_id',
          align: 'left',
        },
        {
          label: 'Protocol',
          name: 'protocol',
          field: 'protocol',
          align: 'left',
        },
        {
          label: 'Port numbers',
          name: 'port_numbers',
          field: 'port_numbers',
          align: 'left',
        },
        {
          label: 'Details',
          name: 'comment',
          field: 'comment',
          align: 'left',
        },
        {
          label: 'Change Request',
          name: 'last_change_link',
          field: 'last_change_link',
          align: 'center',
        },
        {
          label: 'Created at',
          name: 'created_at',
          field: 'created_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updated_at',
          field: 'updated_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
    };
  },
  computed: {
    computedCols() {
      return this.showIps
        ? [...this.baseCols.slice(0, 3), ...this.ipCols, ...this.baseCols.slice(3)]
        : [...this.baseCols.slice(0, 3), ...this.segmentCols, ...this.baseCols.slice(3)];
    },
  },
  methods: {
    copyValue(value) {
      copyToClipboard(value).then(() => {
        this.$notify('info', 'Info', 'IP was copied');
      });
    },
    goToRegistration(event, record) {
      supportCntrlClick(
        event,
        `/my-portfolio/${record.registration?.digitalService?.apmId}/registration/${record.registration.id}?itemId=${record.id}&source=${record.source_apm_id}`,
      );
    },
    showWorkflow(event, record) {
      supportCntrlClick(event, `/workflows?itemId=${record.id}`);
    },
    async load(props) {
      this.loading = true;
      try {
        this.relatedWorkflowStatuses = [];
        const limit = props.pagination.rowsPerPage;
        const { sortBy, descending } = props.pagination;
        const skip = props.pagination.page === 1 ? 0 : (props.pagination.page - 1) * props.pagination.rowsPerPage;
        const {
          id,
          registrationId,
          infraService,
          changeNumber,
          status,
          sourceIp,
          targetIp,
          sourceApmId,
          targetApmId,
          sourceServiceName,
          targetServiceName,
          sourceEnv,
          targetEnv,
          sourceDatacenter,
          targetDatacenter,
          port,
          protocol,
          dateFrom,
          dateTo,
        } = this.filters;
        const { count, data } = await getFirewallRules(
          limit,
          skip,
          sortBy,
          descending,
          id,
          registrationId,
          infraService,
          changeNumber,
          status,
          sourceIp,
          targetIp,
          sourceApmId,
          targetApmId,
          sourceServiceName,
          targetServiceName,
          sourceEnv,
          targetEnv,
          sourceDatacenter,
          targetDatacenter,
          port,
          protocol,
          dateFrom,
          dateTo,
        );
        this.pagination.rowsNumber = count;
        this.rows = data;
        this.pagination.page = props.pagination.page;
        this.pagination.sortBy = sortBy;
        this.pagination.descending = descending;
        this.pagination.rowsPerPage = props.pagination.rowsPerPage;
        if (this.mode === 'list') {
          this.$router.replace({ query: this.buildQueryFromFilters() });
        }
        const relatedWorkflowsPrmises = this.rows.map((record) => {
          return getRelatedWorkflowsStatuses(record.id).then((response) => {
            this.relatedWorkflowStatuses.push({ id: record.id, statuses: response });
          });
        });
        await Promise.all([...relatedWorkflowsPrmises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
    reload() {
      this.$refs.itemsTable.requestServerInteraction();
    },
  },
  mounted() {
    if (this.mode === 'list') {
      const query = this.$route.query;
      const sortFields = ['created_at', 'updated_at'];

      for (const field of sortFields) {
        if (query?.[field]) {
          this.pagination.sortBy = field;
          this.pagination.descending = query[field] !== 'ASC';
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.long-desc {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.short {
    max-width: 100px;
  }
}
</style>
